import { Monitor, Moon, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Tab,
  TabList,
  TabPanel,
  Tabs as AriaTabs,
  ToggleButton,
  ToggleButtonGroup,
  type Key,
} from 'react-aria-components';
import type { ThemePreference } from '@hopex/tokens';
import { cx } from '../cx.ts';
import { useTheme } from '../theme/theme.tsx';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

export function Tabs({
  items,
  label,
  defaultSelected,
}: {
  items: TabItem[];
  label: string;
  defaultSelected?: string;
}) {
  return (
    <AriaTabs defaultSelectedKey={defaultSelected ?? items[0]?.id}>
      <TabList aria-label={label} className="flex gap-1 rounded-md bg-surface-2 p-1">
        {items.map((t) => (
          <Tab
            key={t.id}
            id={t.id}
            className={cx(
              'flex-1 cursor-pointer rounded-sm px-3 py-2 text-center text-[13px] font-semibold text-fg-muted outline-hidden transition-colors',
              'hover:text-fg data-[selected]:bg-surface-1 data-[selected]:text-fg data-[selected]:shadow-card',
              'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus',
            )}
          >
            {t.label}
          </Tab>
        ))}
      </TabList>
      {items.map((t) => (
        <TabPanel key={t.id} id={t.id} className="pt-4 outline-hidden">
          {t.content}
        </TabPanel>
      ))}
    </AriaTabs>
  );
}

/** Single-select filter chips (e.g. market categories). Selected chip: gold fill, ink text. */
export function FilterChips({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <ToggleButtonGroup
      aria-label={label}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys: Set<Key>) => {
        const [first] = [...keys];
        if (first !== undefined) onChange(String(first));
      }}
      className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]"
    >
      {options.map((o) => (
        <ToggleButton
          key={o.id}
          id={o.id}
          className={cx(
            'shrink-0 cursor-pointer rounded-full border border-line bg-surface-1 px-3.5 py-2 text-xs font-medium text-fg-muted outline-hidden',
            'hover:text-fg data-[selected]:border-accent data-[selected]:bg-accent data-[selected]:font-bold data-[selected]:text-on-accent',
            'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:outline-offset-2',
          )}
        >
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

const THEME_OPTIONS: { id: ThemePreference; label: string; icon: typeof Moon }[] = [
  { id: 'dark', label: 'تیره', icon: Moon },
  { id: 'light', label: 'روشن', icon: Sun },
  { id: 'system', label: 'سیستم', icon: Monitor },
];

/** Dark / Light / System. `compact` shows icons only (with accessible names). */
export function ThemeToggle({ compact }: { compact?: boolean }) {
  const { preference, setPreference } = useTheme();
  return (
    <ToggleButtonGroup
      aria-label="حالت نمایش"
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[preference]}
      onSelectionChange={(keys: Set<Key>) => {
        const [first] = [...keys];
        if (first === 'dark' || first === 'light' || first === 'system') setPreference(first);
      }}
      className="inline-flex rounded-full border border-line bg-surface-1 p-1"
    >
      {THEME_OPTIONS.map(({ id, label, icon: Icon }) => (
        <ToggleButton
          key={id}
          id={id}
          aria-label={label}
          className={cx(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-full text-xs font-medium text-fg-muted outline-hidden transition-colors',
            compact ? 'size-8 justify-center' : 'h-8 px-3',
            'hover:text-fg data-[selected]:bg-surface-3 data-[selected]:text-fg',
            'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus',
          )}
        >
          <Icon aria-hidden className="size-4" />
          {!compact && label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
